import { Link } from "react-router-dom";
import { ArrowRight, Clock, ShieldCheck, Lock, BadgeDollarSign, Siren } from "lucide-react";
import { MOBILE_STEPS, IMAGES } from "../data/content";
import { PageHero, Reveal, Eyebrow, useSeo } from "../components/Reveal";
import { UrgentButton } from "../components/UrgentModal";

const BENEFITS = [
  {
    icon: Clock,
    title: "Hours back, every time",
    desc: "No clinic trips, no waiting rooms. Employees are typically done in under 15 minutes and straight back to work.",
  },
  {
    icon: BadgeDollarSign,
    title: "Lower total cost",
    desc: "Cut lost productivity, mileage, and overtime. One mobile visit can cover your entire crew at once.",
  },
  {
    icon: Lock,
    title: "Private by design",
    desc: "Collections happen in a private area at your site — no shared waiting rooms, no awkward encounters.",
  },
  {
    icon: ShieldCheck,
    title: "Fully compliant",
    desc: "Certified collectors, sealed chain-of-custody, and certified labs keep you aligned with DOT and state requirements.",
  },
];

export default function MobileTesting() {
  useSeo(
    "Mobile Testing — We Come to You | SwiftTest Solutions Eastern Iowa",
    "How SwiftTest mobile testing works: certified collectors travel to your office, job site, or home with last-minute and 24/7 after-hours dispatch across Eastern Iowa."
  );

  return (
    <div data-testid="mobile-testing-page">
      <PageHero
        testId="mobile-testing-hero"
        eyebrow="Mobile testing"
        title="The testing site is wherever you are"
        lead="SwiftTest was built mobile from day one. Here's exactly how it works when we come to you — and why businesses across Eastern Iowa never go back to clinic-based testing."
      />

      {/* BENEFITS */}
      <section className="bg-background">
        <div className="mx-auto max-w-7xl px-5 py-20 sm:px-8">
          <Reveal className="max-w-2xl">
            <Eyebrow>Why mobile</Eyebrow>
            <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              Mobile testing saves time, money, and headaches
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {BENEFITS.map((b, i) => (
              <Reveal key={b.title} delay={i * 0.07}>
                <div
                  data-testid={`mobile-benefit-${i}`}
                  className="h-full rounded-3xl border border-slate-200 bg-white p-7 shadow-sm"
                >
                  <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-teal-50 text-teal-700">
                    <b.icon className="h-6 w-6" />
                  </span>
                  <h3 className="mt-5 font-display text-lg font-bold text-slate-900">{b.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{b.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* STEPS */}
      <section className="bg-white">
        <div className="mx-auto max-w-7xl px-5 py-20 sm:px-8 sm:py-24">
          <div className="grid items-start gap-14 lg:grid-cols-2">
            <Reveal>
              <Eyebrow>Step by step</Eyebrow>
              <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
                What happens when we arrive
              </h2>
              <div className="mt-10 space-y-0">
                {MOBILE_STEPS.map((step, i) => (
                  <div key={step.title} data-testid={`mobile-step-${i}`} className="relative flex gap-5 pb-10 last:pb-0">
                    {i < MOBILE_STEPS.length - 1 && (
                      <span className="absolute left-[22px] top-12 h-full w-px bg-teal-200" aria-hidden="true" />
                    )}
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-teal-700 font-display text-sm font-bold text-white shadow-md">
                      {i + 1}
                    </span>
                    <div>
                      <h3 className="font-display text-lg font-bold text-slate-900">{step.title}</h3>
                      <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{step.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="lg:sticky lg:top-28">
                <div className="relative">
                  <div className="absolute -inset-3 -rotate-2 rounded-[2.2rem] border-2 border-teal-200" aria-hidden="true" />
                  <img
                    data-testid="mobile-testing-image"
                    src={IMAGES.custody.url}
                    alt={IMAGES.custody.alt}
                    className="relative aspect-[4/5] w-full rounded-[2rem] object-cover shadow-2xl sm:aspect-[4/4]"
                  />
                </div>
                <div className="mt-8 rounded-3xl border border-slate-200 bg-background p-6">
                  <h3 className="flex items-center gap-2 font-display text-lg font-bold text-slate-900">
                    <Lock className="h-5 w-5 text-teal-700" /> On-site privacy protocol
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">
                    Every visit includes identity verification, a private collection area,
                    tamper-evident sealing, and documented chain-of-custody — handled discreetly so
                    your team's dignity and your compliance are both protected.
                  </p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* EMERGENCY BAND */}
      <section className="bg-background">
        <div className="mx-auto max-w-7xl px-5 pb-24 sm:px-8">
          <Reveal>
            <div className="flex flex-col items-start gap-6 rounded-[2.5rem] border border-amber-200 bg-amber-50 p-8 sm:flex-row sm:items-center sm:justify-between sm:p-12">
              <div className="flex items-start gap-4">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-amber-500 text-white">
                  <Siren className="h-6 w-6" />
                </span>
                <div>
                  <h2 className="font-display text-2xl font-bold tracking-tight text-amber-950">
                    Last-minute or after-hours? We dispatch 24/7.
                  </h2>
                  <p className="mt-2 max-w-xl text-sm leading-relaxed text-amber-900/80 sm:text-base">
                    Post-accident and reasonable-suspicion situations can't wait until morning. Call
                    or text any time — nights, weekends, holidays — and a certified collector heads
                    your way.
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap gap-3">
                <UrgentButton testId="mobile-need-testing-now-button" className="px-6 py-3.5 text-base" />
                <Link
                  data-testid="mobile-request-link"
                  to="/contact"
                  className="inline-flex items-center gap-2 rounded-full border-2 border-amber-900/30 px-6 py-3 text-sm font-bold text-amber-950 transition-all hover:-translate-y-0.5 hover:bg-amber-100"
                >
                  Plan a visit <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
